/**
 * Owner-facing incident callouts on a walk (My Walks). Shows each incident the
 * walker reported, with urgent emphasis, photos, resolution, and an Acknowledge
 * action. Reported incidents appear live via the My Walks 15s poll.
 */
import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImageCarousel } from '@/components/image-carousel';
import { use_acknowledge_incident } from '@/features/walk-incident/hooks';
import { INCIDENT_TYPE_LABEL } from '@/features/walk-incident/types';
import type { Booking } from '@/features/booking/types';

export function OwnerWalkIncidents({ booking }: { booking: Booking }) {
  const ack_m = use_acknowledge_incident(booking.id);
  const incidents = booking.incidents ?? [];
  if (incidents.length === 0) return null;

  return (
    <div className="flex basis-full flex-col gap-2" data-testid={`walk-incidents-${booking.id}`}>
      {incidents.map((i) => {
        const urgent = i.severity === 'URGENT';
        const open = i.status === 'OPEN';
        const accent = urgent && open ? 'border-destructive/30 bg-destructive/5' : 'border-brand-outline-variant/20 bg-muted/30';
        return (
          <div key={i.id} className={`rounded-card border-2 p-3 ${accent}`} data-testid={`incident-${i.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${urgent && open ? 'text-destructive' : 'text-muted-foreground'}`} />
              <span className="font-semibold">{INCIDENT_TYPE_LABEL[i.type]}</span>
              {urgent && (
                <span className="rounded-full bg-destructive/12 px-2 py-0.5 text-[11px] font-medium text-destructive">urgent</span>
              )}
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${open ? 'bg-warning/15 text-warning' : 'bg-success/12 text-success'}`}>
                {open ? 'open' : 'resolved'}
              </span>
            </div>
            <p className="mt-1 text-sm">{i.note}</p>
            {i.photos.length > 0 && (
              <div className="mt-2">
                <ImageCarousel images={i.photos} />
              </div>
            )}
            {i.resolution_note && <p className="mt-1 text-xs text-success">Resolved: {i.resolution_note}</p>}

            <div className="mt-2">
              {i.owner_acknowledged_at ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground" data-testid={`incident-acknowledged-${i.id}`}>
                  <Check className="h-3.5 w-3.5" /> Acknowledged
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={ack_m.isPending}
                  onClick={() => ack_m.mutate(i.id)}
                  data-testid={`incident-ack-${i.id}`}
                >
                  {ack_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Acknowledge
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
